import {
  MouseEvent,
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useHotkeys } from "domains/commons/contexts/HotkeysProvider";
import usePersistedState, {
  PersistedStateKey,
} from "domains/commons/hooks/usePersistedState";
import { simulateWheel } from "domains/commons/utils/simulateWheel";
import { appShortcuts } from "domains/shortcuts/components/appShortcuts";
import { GetAssetsByAssetIdApiResponse } from "infra/api/generated/api";
import * as THREE from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";

import { Box, Flex, HStack } from "@chakra-ui/react";
import { OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";

import { CameraController } from "./CameraController";
import { EnhancedLighting } from "./EnhancedLighting";
import { ThreeDModelLoader, WireframeLoader } from "./LoadingComponents";
import { Sidebar } from "./Sidebar";
import { StudioEnvironment } from "./StudioEnvironment";
import { ThreeViewer } from "./ThreeViewer";
import type { CameraPosition, DisplaySettings, Theme } from "./types";

const DEFAULT_DISPLAY_SETTINGS: DisplaySettings = {
  // Theme defaults
  theme: "light",
  themeIntensity: 1.0,
  // Lighting defaults
  environmentIntensity: 0.3,
  enableShadows: true,
  brightness: 2,
  contrast: 1,
  rimLightIntensity: 0.35,
  // Object display defaults
  wireframe: false,
  wireframeOnly: false,
  previousWireframeState: false,
  // Camera defaults
  cameraPosition: "default",
};

interface AssetZoom3DViewerProps {
  /** Asset data containing the 3D model URL and metadata */
  asset: GetAssetsByAssetIdApiResponse["asset"];
}

/**
 * Main 3D asset viewer component with enhanced lighting and controls
 *
 * Features:
 * - Professional 3-point lighting system
 * - Real-time shadows with high-quality shadow maps
 * - Environment mapping with HDR studio lighting
 * - Advanced material handling preserving PBR properties
 * - Interactive controls for brightness, contrast, shadows, etc.
 * - Performance optimizations for smooth rendering
 * - Loading states for expensive operations like wireframe processing
 * - Studio ambiance with infinite floor and background
 * - Scene mode selection between dark and light themes
 *
 * @param props - Component props containing asset data
 * @returns JSX element containing the 3D viewer interface
 */
export default function AssetZoom3DViewer({ asset }: AssetZoom3DViewerProps) {
  const orbitRef = useRef<OrbitControlsImpl>(null);
  const wireframeTimeoutRef = useRef<NodeJS.Timeout>();
  const wireframeOnlyTimeoutRef = useRef<NodeJS.Timeout>();
  const wireframeProcessedTimeoutRef = useRef<NodeJS.Timeout>();

  const [settings, setSettings] = usePersistedState<DisplaySettings>(
    PersistedStateKey.ASSET_ZOOM_3D_VIEWER_SETTINGS,
    {
      defaultValue: DEFAULT_DISPLAY_SETTINGS,
    }
  );
  const [isWireframeProcessing, setIsWireframeProcessing] = useState(false);
  const [isPositioningCamera, setIsPositioningCamera] = useState(false);

  useHotkeys(
    appShortcuts.assetPage.shortcuts.resetZoom.shortcut,
    () => {
      if (orbitRef.current) {
        // Reset orbit controls (CameraController will reposition appropriately)
        orbitRef.current.reset();
      }
    },
    []
  );
  useHotkeys(
    appShortcuts.assetPage.shortcuts.zoomInArrow.shortcut,
    () => simulateWheel(-100),
    []
  );
  useHotkeys(
    appShortcuts.assetPage.shortcuts.zoomOutArrow.shortcut,
    () => simulateWheel(100),
    []
  );

  /**
   * Resets all display settings to their default values
   * @param e - Mouse event from the reset button click
   */
  const handleResetSettings = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setSettings(DEFAULT_DISPLAY_SETTINGS);
    },
    [setSettings]
  );

  /**
   * Handles theme selection change
   * @param theme - The selected theme (dark or light)
   */
  const handleThemeChange = useCallback(
    (theme: Theme) => {
      setSettings((prevSettings) => ({
        ...prevSettings,
        theme: theme,
      }));
    },
    [setSettings]
  );

  /**
   * Handles camera position selection change
   * @param position - The selected camera position preset
   */
  const handleCameraPositionChange = useCallback(
    (position: CameraPosition) => {
      // Set positioning flag to prevent manual detection during preset positioning
      setIsPositioningCamera(true);

      setSettings((prevSettings) => ({
        ...prevSettings,
        cameraPosition: position,
      }));
    },
    [setSettings, setIsPositioningCamera]
  );

  /**
   * Handles wireframe toggle with loading state
   * Shows loader during wireframe processing for better UX
   * Includes minimum display time to ensure visibility
   * Only works when wireframe-only is not active (since wireframe is locked in that mode)
   */
  const handleWireframeToggle = useCallback(() => {
    if (settings.wireframeOnly) {
      // Wireframe is locked when wireframe-only is active
      return;
    }

    // Clear any existing timeout
    if (wireframeTimeoutRef.current) {
      clearTimeout(wireframeTimeoutRef.current);
    }

    setIsWireframeProcessing(true);

    // Add small delay to ensure loader shows before processing starts
    wireframeTimeoutRef.current = setTimeout(() => {
      setSettings((prevSettings) => ({
        ...prevSettings,
        wireframe: !prevSettings.wireframe,
      }));
    }, 100);
  }, [setSettings, settings.wireframeOnly]);

  /**
   * Handles wireframe-only toggle with automatic wireframe management
   * When enabled: stores current wireframe state and forces wireframe on
   * When disabled: restores previous wireframe state
   * Shows processing loader for visual feedback
   */
  const handleWireframeOnlyToggle = useCallback(() => {
    // Clear any existing timeout
    if (wireframeOnlyTimeoutRef.current) {
      clearTimeout(wireframeOnlyTimeoutRef.current);
    }

    setIsWireframeProcessing(true);

    // Add small delay to ensure loader shows before processing starts
    wireframeOnlyTimeoutRef.current = setTimeout(() => {
      setSettings((prevSettings) => {
        const newWireframeOnly = !prevSettings.wireframeOnly;

        if (newWireframeOnly) {
          // Activating wireframe-only: store current wireframe state and force wireframe on
          return {
            ...prevSettings,
            wireframeOnly: true,
            wireframe: true, // Force wireframe on
            previousWireframeState: prevSettings.wireframe, // Store current state
          };
        } else {
          // Deactivating wireframe-only: restore previous wireframe state
          return {
            ...prevSettings,
            wireframeOnly: false,
            wireframe: prevSettings.previousWireframeState, // Restore previous state
          };
        }
      });
    }, 100);
  }, [setSettings]);

  /**
   * Handles wireframe processing completion with minimum display duration
   */
  const handleWireframeProcessed = useCallback(() => {
    // Clear any existing timeout
    if (wireframeProcessedTimeoutRef.current) {
      clearTimeout(wireframeProcessedTimeoutRef.current);
    }

    // Ensure loader is visible for at least 800ms for better UX
    wireframeProcessedTimeoutRef.current = setTimeout(() => {
      setIsWireframeProcessing(false);
    }, 800);
  }, []);

  /**
   * Detects when user manually manipulates camera and sets position to "manual"
   */
  const handleCameraManualChange = useCallback(() => {
    // Use functional update to get current state and avoid stale closures
    if (!isPositioningCamera) {
      setSettings((prevSettings) => {
        if (prevSettings.cameraPosition !== "manual") {
          return {
            ...prevSettings,
            cameraPosition: "manual",
          };
        } else {
          return prevSettings;
        }
      });
    }
  }, [setSettings, isPositioningCamera]);

  // Setup orbit controls camera change detection
  useEffect(() => {
    const orbitControls = orbitRef.current;
    if (!orbitControls) return;

    // Listen for changes to orbit controls (user dragging/rotating camera)
    orbitControls.addEventListener("change", handleCameraManualChange);

    return () => {
      orbitControls.removeEventListener("change", handleCameraManualChange);
    };
  }, [handleCameraManualChange]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (wireframeTimeoutRef.current) {
        clearTimeout(wireframeTimeoutRef.current);
      }
      if (wireframeOnlyTimeoutRef.current) {
        clearTimeout(wireframeOnlyTimeoutRef.current);
      }
      if (wireframeProcessedTimeoutRef.current) {
        clearTimeout(wireframeProcessedTimeoutRef.current);
      }
    };
  }, []);

  return (
    <Flex pos="relative" flex={1} w="100%" cursor="move" bgColor="black">
      <Box pos="absolute" top={0} right={0} bottom={0} left={0}>
        <HStack w="full" h="full" bgColor="black" spacing={0}>
          <Sidebar
            settings={settings}
            isWireframeProcessing={isWireframeProcessing}
            onSettingsChange={setSettings}
            onThemeChange={handleThemeChange}
            onCameraPositionChange={handleCameraPositionChange}
            onWireframeToggle={handleWireframeToggle}
            onWireframeOnlyToggle={handleWireframeOnlyToggle}
            onResetSettings={handleResetSettings}
          />

          <Box
            pos="relative"
            overflow="hidden"
            w="full"
            h="full"
            cursor={"move"}
          >
            <Canvas
              camera={{ position: [0, 1, 3], far: 1_000 }}
              shadows={settings.enableShadows ? "soft" : false}
              gl={{
                antialias: true,
                alpha: false,
                powerPreference: "high-performance",
                toneMapping: THREE.ACESFilmicToneMapping,
                toneMappingExposure: 1.2,
              }}
            >
              <Suspense fallback={<ThreeDModelLoader />}>
                <StudioEnvironment
                  theme={settings.theme}
                  environmentIntensity={settings.environmentIntensity}
                  themeIntensity={settings.themeIntensity}
                />
                <EnhancedLighting
                  brightness={settings.brightness}
                  rimLightIntensity={settings.rimLightIntensity}
                  enableShadows={settings.enableShadows}
                  theme={settings.theme}
                />
                <OrbitControls
                  ref={orbitRef}
                  minDistance={0.1}
                  maxDistance={10}
                  enableDamping
                  dampingFactor={0.05}
                />
                <ThreeViewer
                  assetUrl={asset.url}
                  orbitRef={orbitRef}
                  settings={settings}
                  onWireframeProcessed={handleWireframeProcessed}
                />
                <CameraController
                  orbitRef={orbitRef}
                  cameraPosition={settings.cameraPosition}
                  setIsPositioningCamera={setIsPositioningCamera}
                />
                {/* Wireframe processing loader */}
                {isWireframeProcessing && <WireframeLoader />}
              </Suspense>
            </Canvas>
          </Box>
        </HStack>
      </Box>
    </Flex>
  );
}
